/**
 * Self-contained QR code encoder (ISO/IEC 18004), no dependencies.
 *
 * Why hand-written: the business card's QR is the product. It must work
 * offline, inside a Teams tab, and must never depend on an external image
 * service (the previous Power App fetched its QR from a PHP endpoint on an
 * Azure website, which is exactly the dependency this replaces) or on an npm
 * package whose supply chain we would have to trust for every scan.
 *
 * Scope: byte mode only (the payload is UTF-8 encoded first, which every
 * phone camera decodes as text), versions 1–40, all four error-correction
 * levels, automatic version selection and automatic mask selection by the
 * standard penalty rules. Output is a boolean module matrix; rendering (SVG,
 * canvas) is the caller's job — see `components/qr-code.tsx`.
 *
 * The constants below are the published tables from the standard
 * (error-correction codewords per block and block counts per version).
 */

export type EccLevel = "L" | "M" | "Q" | "H";

export interface QrMatrix {
  /** Side length in modules (21 for version 1, +4 per version). */
  size: number;
  /** Symbol version 1–40. */
  version: number;
  /** Error-correction level actually used. */
  ecc: EccLevel;
  /** Mask pattern 0–7 that was selected. */
  mask: number;
  /** `modules[y][x]` is true for a dark module. */
  modules: boolean[][];
}

const ECC_FORMAT_BITS: Record<EccLevel, number> = { L: 1, M: 0, Q: 3, H: 2 };
const ECC_INDEX: Record<EccLevel, number> = { L: 0, M: 1, Q: 2, H: 3 };

// Indexed [eccIndex][version]; index 0 is a placeholder so version N is at [N].
const ECC_CODEWORDS_PER_BLOCK: number[][] = [
  [
    -1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30,
    30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30,
  ],
  [
    -1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28,
    28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28,
  ],
  [
    -1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30,
    30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30,
  ],
  [
    -1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30,
    30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30,
  ],
];

const NUM_ECC_BLOCKS: number[][] = [
  [
    -1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14,
    15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25,
  ],
  [
    -1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23,
    25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49,
  ],
  [
    -1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34,
    34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68,
  ],
  [
    -1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35,
    37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81,
  ],
];

const PENALTY_N1 = 3;
const PENALTY_N2 = 3;
const PENALTY_N3 = 40;
const PENALTY_N4 = 10;

/**
 * Encode `text` into a QR module matrix.
 *
 * Picks the smallest version that fits at the requested error-correction
 * level and the mask with the lowest penalty score. Throws when the payload
 * exceeds version 40 at that level (about 2.3 kB at level L).
 */
export function encodeQr(text: string, ecc: EccLevel = "M"): QrMatrix {
  const bytes = new TextEncoder().encode(text);
  const version = chooseVersion(bytes.length, ecc);
  const dataCodewords = buildDataCodewords(bytes, version, ecc);
  const allCodewords = addEccAndInterleave(dataCodewords, version, ecc);
  return placeAndMask(allCodewords, version, ecc);
}

/** Smallest version whose byte-mode capacity holds `byteCount` bytes. */
function chooseVersion(byteCount: number, ecc: EccLevel): number {
  for (let version = 1; version <= 40; version++) {
    const capacityBits = numDataCodewords(version, ecc) * 8;
    const neededBits = 4 + charCountBits(version) + byteCount * 8;
    if (neededBits <= capacityBits) {
      return version;
    }
  }
  throw new Error(`QR payload too long (${byteCount} bytes) for error-correction level ${ecc}`);
}

/** Byte-mode character-count field width for a version. */
function charCountBits(version: number): number {
  return version <= 9 ? 8 : 16;
}

/** Total raw data modules available for codewords in a version. */
function numRawDataModules(version: number): number {
  let result = (16 * version + 128) * version + 64;
  if (version >= 2) {
    const numAlign = Math.floor(version / 7) + 2;
    result -= (25 * numAlign - 10) * numAlign - 55;
    if (version >= 7) {
      result -= 36;
    }
  }
  return result;
}

/** Error-correction parameters for a version/level from the published tables. */
function eccParams(version: number, ecc: EccLevel): { perBlock: number; numBlocks: number } {
  const idx = ECC_INDEX[ecc];
  const perBlock = ECC_CODEWORDS_PER_BLOCK[idx]?.[version];
  const numBlocks = NUM_ECC_BLOCKS[idx]?.[version];
  if (perBlock === undefined || numBlocks === undefined || perBlock < 0) {
    throw new Error(`No QR parameters for version ${version} level ${ecc}`);
  }
  return { perBlock, numBlocks };
}

/** Number of data codewords (total codewords minus error-correction codewords). */
function numDataCodewords(version: number, ecc: EccLevel): number {
  const { perBlock, numBlocks } = eccParams(version, ecc);
  return Math.floor(numRawDataModules(version) / 8) - perBlock * numBlocks;
}

/** Mode indicator + count + payload + terminator + pad bytes, as codewords. */
function buildDataCodewords(bytes: Uint8Array, version: number, ecc: EccLevel): number[] {
  const bits: number[] = [];
  const pushBits = (value: number, length: number) => {
    for (let i = length - 1; i >= 0; i--) {
      bits.push((value >>> i) & 1);
    }
  };
  pushBits(0b0100, 4);
  pushBits(bytes.length, charCountBits(version));
  for (const byte of bytes) {
    pushBits(byte, 8);
  }
  const capacityBits = numDataCodewords(version, ecc) * 8;
  pushBits(0, Math.min(4, capacityBits - bits.length));
  while (bits.length % 8 !== 0) {
    bits.push(0);
  }
  for (let pad = 0xec; bits.length < capacityBits; pad ^= 0xec ^ 0x11) {
    pushBits(pad, 8);
  }
  const codewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let value = 0;
    for (let j = 0; j < 8; j++) {
      value = (value << 1) | (bits[i + j] ?? 0);
    }
    codewords.push(value);
  }
  return codewords;
}

/** Split into blocks, append Reed–Solomon codewords, interleave per the standard. */
function addEccAndInterleave(data: number[], version: number, ecc: EccLevel): number[] {
  const { perBlock: blockEccLen, numBlocks } = eccParams(version, ecc);
  const rawCodewords = Math.floor(numRawDataModules(version) / 8);
  const numShortBlocks = numBlocks - (rawCodewords % numBlocks);
  const shortBlockLen = Math.floor(rawCodewords / numBlocks);

  const generator = rsGenerator(blockEccLen);
  const blocks: number[][] = [];
  for (let i = 0, k = 0; i < numBlocks; i++) {
    const dataLen = shortBlockLen - blockEccLen + (i < numShortBlocks ? 0 : 1);
    const block = data.slice(k, k + dataLen);
    k += dataLen;
    const eccCodewords = rsRemainder(block, generator);
    if (i < numShortBlocks) {
      block.push(0); // placeholder so every block has the long length; skipped on interleave
    }
    blocks.push(block.concat(eccCodewords));
  }

  const result: number[] = [];
  const longBlockLen = blocks[0]?.length ?? 0;
  for (let i = 0; i < longBlockLen; i++) {
    blocks.forEach((block, j) => {
      const codeword = block[i];
      if (codeword !== undefined && (i !== shortBlockLen - blockEccLen || j >= numShortBlocks)) {
        result.push(codeword);
      }
    });
  }
  return result;
}

/** Reed–Solomon generator polynomial of the given degree over GF(2^8). */
function rsGenerator(degree: number): number[] {
  let result = [1];
  let root = 1;
  for (let i = 0; i < degree; i++) {
    // Coefficients are kept highest degree first: multiplying by (x + root)
    // shifts the existing terms up one degree and adds root times each term.
    const next = new Array<number>(result.length + 1).fill(0);
    for (let j = 0; j < result.length; j++) {
      const coefficient = result[j] ?? 0;
      next[j] = (next[j] ?? 0) ^ coefficient;
      next[j + 1] = (next[j + 1] ?? 0) ^ gfMultiply(coefficient, root);
    }
    result = next;
    root = gfMultiply(root, 0x02);
  }
  return result;
}

/** Remainder of `data` divided by `generator`, i.e. the error-correction codewords. */
function rsRemainder(data: number[], generator: number[]): number[] {
  const degree = generator.length - 1;
  const result = new Array<number>(degree).fill(0);
  for (const byte of data) {
    const factor = byte ^ (result[0] ?? 0);
    result.shift();
    result.push(0);
    for (let i = 0; i < degree; i++) {
      result[i] = (result[i] ?? 0) ^ gfMultiply(generator[i + 1] ?? 0, factor);
    }
  }
  return result;
}

/** Multiply in GF(2^8) with the QR reducing polynomial x^8 + x^4 + x^3 + x^2 + 1. */
function gfMultiply(x: number, y: number): number {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
}

/** Draw function patterns, place codewords, choose and apply the best mask. */
function placeAndMask(codewords: number[], version: number, ecc: EccLevel): QrMatrix {
  const size = version * 4 + 17;
  const modules: boolean[][] = Array.from({ length: size }, () =>
    new Array<boolean>(size).fill(false),
  );
  const isFunction: boolean[][] = Array.from({ length: size }, () =>
    new Array<boolean>(size).fill(false),
  );

  const setFunction = (x: number, y: number, dark: boolean) => {
    setCell(modules, x, y, dark);
    setCell(isFunction, x, y, true);
  };

  // Timing patterns.
  for (let i = 0; i < size; i++) {
    setFunction(6, i, i % 2 === 0);
    setFunction(i, 6, i % 2 === 0);
  }
  // Finder patterns with separators.
  drawFinder(3, 3, size, setFunction);
  drawFinder(size - 4, 3, size, setFunction);
  drawFinder(3, size - 4, size, setFunction);
  // Alignment patterns (skip the three that overlap finders).
  const align = alignmentPositions(version);
  align.forEach((cx, i) => {
    align.forEach((cy, j) => {
      const overlapsFinder =
        (i === 0 && j === 0) ||
        (i === 0 && j === align.length - 1) ||
        (i === align.length - 1 && j === 0);
      if (!overlapsFinder) {
        drawAlignment(cx, cy, setFunction);
      }
    });
  });
  // Reserve format and version areas (real bits are written after masking).
  drawFormatBits(0, ecc, size, setFunction);
  drawVersionBits(version, size, setFunction);

  // Place codewords in the zigzag order.
  let bitIndex = 0;
  const totalBits = codewords.length * 8;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) {
      right = 5;
    }
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        const upward = ((right + 1) & 2) === 0;
        const y = upward ? size - 1 - vert : vert;
        if (!getCell(isFunction, x, y) && bitIndex < totalBits) {
          const codeword = codewords[bitIndex >>> 3] ?? 0;
          setCell(modules, x, y, ((codeword >>> (7 - (bitIndex & 7))) & 1) !== 0);
          bitIndex++;
        }
      }
    }
  }

  // Try every mask, keep the one with the lowest penalty.
  let bestMask = 0;
  let bestPenalty = Number.POSITIVE_INFINITY;
  for (let mask = 0; mask < 8; mask++) {
    applyMask(modules, isFunction, mask);
    drawFormatBits(mask, ecc, size, setFunction);
    const penalty = penaltyScore(modules);
    if (penalty < bestPenalty) {
      bestPenalty = penalty;
      bestMask = mask;
    }
    applyMask(modules, isFunction, mask); // XOR again to undo
  }
  applyMask(modules, isFunction, bestMask);
  drawFormatBits(bestMask, ecc, size, setFunction);

  return { size, version, ecc, mask: bestMask, modules };
}

type SetFn = (x: number, y: number, dark: boolean) => void;

/** Read a cell of a square grid; out-of-range reads as false. */
function getCell(grid: boolean[][], x: number, y: number): boolean {
  return grid[y]?.[x] ?? false;
}

/** Write a cell of a square grid (no-op out of range). */
function setCell(grid: boolean[][], x: number, y: number, value: boolean): void {
  const row = grid[y];
  if (row !== undefined && x >= 0 && x < row.length) {
    row[x] = value;
  }
}

/** 7×7 finder pattern plus its one-module light separator, clipped to the symbol. */
function drawFinder(cx: number, cy: number, size: number, set: SetFn): void {
  for (let dy = -4; dy <= 4; dy++) {
    for (let dx = -4; dx <= 4; dx++) {
      const x = cx + dx;
      const y = cy + dy;
      if (x >= 0 && x < size && y >= 0 && y < size) {
        const dist = Math.max(Math.abs(dx), Math.abs(dy));
        set(x, y, dist !== 2 && dist !== 4);
      }
    }
  }
}

/** 5×5 alignment pattern centred on (cx, cy). */
function drawAlignment(cx: number, cy: number, set: SetFn): void {
  for (let dy = -2; dy <= 2; dy++) {
    for (let dx = -2; dx <= 2; dx++) {
      set(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
    }
  }
}

/** Centre coordinates of the alignment patterns for a version. */
function alignmentPositions(version: number): number[] {
  if (version === 1) {
    return [];
  }
  const numAlign = Math.floor(version / 7) + 2;
  const size = version * 4 + 17;
  const step = version === 32 ? 26 : Math.ceil((version * 4 + 4) / (numAlign * 2 - 2)) * 2;
  const result = [6];
  for (let pos = size - 7; result.length < numAlign; pos -= step) {
    result.splice(1, 0, pos);
  }
  return result;
}

/** Write the 15-bit format information (ECC level + mask, BCH coded) in both places. */
function drawFormatBits(mask: number, ecc: EccLevel, size: number, set: SetFn): void {
  const data = (ECC_FORMAT_BITS[ecc] << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i++) {
    rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  }
  const bits = ((data << 10) | rem) ^ 0x5412;
  const bit = (i: number) => ((bits >>> i) & 1) !== 0;

  for (let i = 0; i <= 5; i++) {
    set(8, i, bit(i));
  }
  set(8, 7, bit(6));
  set(8, 8, bit(7));
  set(7, 8, bit(8));
  for (let i = 9; i < 15; i++) {
    set(14 - i, 8, bit(i));
  }
  for (let i = 0; i < 8; i++) {
    set(size - 1 - i, 8, bit(i));
  }
  for (let i = 8; i < 15; i++) {
    set(8, size - 15 + i, bit(i));
  }
  set(8, size - 8, true); // the always-dark module
}

/** Write the 18-bit version information for versions 7 and up. */
function drawVersionBits(version: number, size: number, set: SetFn): void {
  if (version < 7) {
    return;
  }
  let rem = version;
  for (let i = 0; i < 12; i++) {
    rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
  }
  const bits = (version << 12) | rem;
  for (let i = 0; i < 18; i++) {
    const dark = ((bits >>> i) & 1) !== 0;
    const a = size - 11 + (i % 3);
    const b = Math.floor(i / 3);
    set(a, b, dark);
    set(b, a, dark);
  }
}

/** XOR the mask pattern onto every non-function module (self-inverse). */
function applyMask(modules: boolean[][], isFunction: boolean[][], mask: number): void {
  const size = modules.length;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (getCell(isFunction, x, y)) {
        continue;
      }
      let invert = false;
      switch (mask) {
        case 0:
          invert = (x + y) % 2 === 0;
          break;
        case 1:
          invert = y % 2 === 0;
          break;
        case 2:
          invert = x % 3 === 0;
          break;
        case 3:
          invert = (x + y) % 3 === 0;
          break;
        case 4:
          invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0;
          break;
        case 5:
          invert = ((x * y) % 2) + ((x * y) % 3) === 0;
          break;
        case 6:
          invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
          break;
        default:
          invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0;
      }
      if (invert) {
        setCell(modules, x, y, !getCell(modules, x, y));
      }
    }
  }
}

/** Penalty score per the standard's four rules — lower is a more scannable symbol. */
function penaltyScore(modules: boolean[][]): number {
  const size = modules.length;
  let result = 0;

  // Rule 1: runs of five or more same-coloured modules in a row/column.
  // Rule 3: finder-like 1:1:3:1:1 patterns with a 4-module light margin.
  for (let y = 0; y < size; y++) {
    let runColor = false;
    let runX = 0;
    const history = [0, 0, 0, 0, 0, 0, 0];
    for (let x = 0; x < size; x++) {
      if (getCell(modules, x, y) === runColor) {
        runX++;
        if (runX === 5) {
          result += PENALTY_N1;
        } else if (runX > 5) {
          result++;
        }
      } else {
        finderPenaltyAddHistory(runX, history, size);
        if (!runColor) {
          result += finderPenaltyCount(history) * PENALTY_N3;
        }
        runColor = getCell(modules, x, y);
        runX = 1;
      }
    }
    result += finderPenaltyTerminate(runColor, runX, history, size) * PENALTY_N3;
  }
  for (let x = 0; x < size; x++) {
    let runColor = false;
    let runY = 0;
    const history = [0, 0, 0, 0, 0, 0, 0];
    for (let y = 0; y < size; y++) {
      if (getCell(modules, x, y) === runColor) {
        runY++;
        if (runY === 5) {
          result += PENALTY_N1;
        } else if (runY > 5) {
          result++;
        }
      } else {
        finderPenaltyAddHistory(runY, history, size);
        if (!runColor) {
          result += finderPenaltyCount(history) * PENALTY_N3;
        }
        runColor = getCell(modules, x, y);
        runY = 1;
      }
    }
    result += finderPenaltyTerminate(runColor, runY, history, size) * PENALTY_N3;
  }

  // Rule 2: 2×2 blocks of the same colour.
  for (let y = 0; y < size - 1; y++) {
    for (let x = 0; x < size - 1; x++) {
      const color = getCell(modules, x, y);
      if (
        color === getCell(modules, x + 1, y) &&
        color === getCell(modules, x, y + 1) &&
        color === getCell(modules, x + 1, y + 1)
      ) {
        result += PENALTY_N2;
      }
    }
  }

  // Rule 4: deviation of the dark-module proportion from 50 %.
  let dark = 0;
  for (const row of modules) {
    for (const module of row) {
      if (module) {
        dark++;
      }
    }
  }
  const total = size * size;
  const k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1;
  result += k * PENALTY_N4;
  return result;
}

/** Count 1:1:3:1:1 finder-like runs in the sliding history window. */
function finderPenaltyCount(history: number[]): number {
  const n = history[1] ?? 0;
  const core =
    n > 0 && history[2] === n && history[3] === n * 3 && history[4] === n && history[5] === n;
  return (
    (core && (history[0] ?? 0) >= n * 4 && (history[6] ?? 0) >= n ? 1 : 0) +
    (core && (history[6] ?? 0) >= n * 4 && (history[0] ?? 0) >= n ? 1 : 0)
  );
}

/** Push a run length onto the history window (padding the first light run as the standard requires). */
function finderPenaltyAddHistory(runLength: number, history: number[], size: number): void {
  if (history[0] === 0) {
    runLength += size; // treat the symbol edge as a light margin
  }
  history.pop();
  history.unshift(runLength);
}

/** Flush the final run of a line and count finder-like patterns at its end. */
function finderPenaltyTerminate(
  runColor: boolean,
  runLength: number,
  history: number[],
  size: number,
): number {
  if (runColor) {
    finderPenaltyAddHistory(runLength, history, size);
    runLength = 0;
  }
  runLength += size;
  finderPenaltyAddHistory(runLength, history, size);
  return finderPenaltyCount(history);
}

/**
 * Render a matrix as an SVG string (one `<path>` for all dark modules).
 *
 * `quietZone` is in modules; the standard asks for four. Colours default to
 * the design-system Norican Blue on white. Returned markup has no XML
 * prologue so it can be inlined or wrapped in a data URL.
 */
export function qrToSvg(
  matrix: QrMatrix,
  options: { quietZone?: number; dark?: string; light?: string; sizePx?: number } = {},
): string {
  const quietZone = options.quietZone ?? 4;
  const dark = options.dark ?? "#03193d";
  const light = options.light ?? "#ffffff";
  const total = matrix.size + quietZone * 2;
  const sizeAttr = options.sizePx ? ` width="${options.sizePx}" height="${options.sizePx}"` : "";
  let path = "";
  for (let y = 0; y < matrix.size; y++) {
    for (let x = 0; x < matrix.size; x++) {
      if (matrix.modules[y]?.[x]) {
        path += `M${x + quietZone} ${y + quietZone}h1v1h-1z`;
      }
    }
  }
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${total} ${total}"${sizeAttr} shape-rendering="crispEdges">` +
    `<rect width="${total}" height="${total}" fill="${light}"/>` +
    `<path d="${path}" fill="${dark}"/>` +
    `</svg>`
  );
}
